import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BaseService } from './base.service';

export type NetdiagType = 'ping' | 'traceroute';

/** Job descriptor returned by `net:ping` / `net:traceroute` and echoed on `net:start`. */
export interface NetdiagJob {
  jobId: string;
  type: NetdiagType;
  target: string;
  command: string;
  pid: number;
  username: string;
  startedAt: number;
  expiresAt: number;
  lines: number;
}

/** One line of command output, in the order the API read it. */
export interface NetdiagLine {
  jobId: string;
  stream: 'stdout' | 'stderr';
  seq: number;
  line: string;
}

/** Why a job stopped. `reason` is finished | killed | timeout | socket_closed | output_limit | spawn_failed. */
export interface NetdiagEnd {
  jobId: string;
  type: NetdiagType;
  target: string;
  code: number | null;
  signal: string | null;
  reason: string;
  message?: string;
  durationMs: number;
  lines: number;
}

export interface NetdiagLimits {
  maxDurationMs: number;
  maxJobsPerSocket: number;
  maxLines: number;
  /** Always 'PERSONNEL'. Required by the API on every net:* command. */
  requiredRole: string;
  /**
   * An extra restriction the deployment may add, and empty when there is none.
   * It can only narrow the gate, never widen it, so visibility is
   * `holds(requiredRole) && (!allowedRoles.length || holdsOneOf(allowedRoles))`.
   * Testing allowedRoles on its own would show the panel to everyone, since it
   * is empty by default.
   */
  allowedRoles: string[];
}

/**
 * Client for the API's `net:*` socket commands (see my-api docs/network-diagnostics.md).
 *
 * ping and traceroute produce output for as long as they run, so they are driven
 * over the existing socket rather than as request/response calls: a command is
 * acknowledged with the job it started, and the job's output arrives afterwards
 * as a stream of `net:data` events ending in a single `net:end`.
 */
@Injectable()
export class NetdiagService extends BaseService {

  // The API acknowledges every command. A missing acknowledgement means the
  // server on the other end predates the feature (or never got the packet), and
  // without this the caller would wait on a promise that can never settle.
  private static readonly ACK_TIMEOUT_MS = 10000;

  /**
   * Issues a command, retrying once after re-authenticating.
   *
   * The API distinguishes NOT_AUTHENTICATED (this socket has not been identified
   * -- re-auth and try again) from NOT_AUTHORIZED (the role is not permitted --
   * retrying never helps). The first is what a reconnect leaves behind, and it
   * is fixable without bothering the operator.
   */
  private async command(event: string, payload: any): Promise<any> {
    try {
      return await this.send(event, payload);
    } catch (error) {
      if (!error || error.message !== 'NOT_AUTHENTICATED') { throw error; }
      // A failure here is not worth reporting on its own: the retry below
      // surfaces the real outcome either way.
      await this.authenticate().catch(() => undefined);
      return this.send(event, payload);
    }
  }

  private send(event: string, payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (settled) { return; }
        settled = true;
        reject(new Error('NETDIAG_UNAVAILABLE'));
      }, NetdiagService.ACK_TIMEOUT_MS);

      this.socket.emit(event, payload, (ack: any) => {
        if (settled) { return; }
        settled = true;
        clearTimeout(timer);
        if (!ack) {
          reject(new Error('NETDIAG_FAILED'));
        } else if (ack.error) {
          reject(new Error(ack.error));
        } else {
          resolve(ack.data);
        }
      });
    });
  }

  /**
   * Starts a job and resolves with it once the process is running. Options are
   * passed through as the API documents them (ping: count/interval/size/timeout,
   * traceroute: max_hops/queries/wait); an out-of-range value is rejected by the
   * API rather than clamped, so the caller should validate before asking.
   */
  start(type: NetdiagType, target: string, options: any = {}): Promise<NetdiagJob> {
    return this.command(`net:${type}`, { target, ...options });
  }

  /** Stops one of this socket's jobs. The API sends SIGINT, so a ping still prints its summary. */
  kill(jobId: string): Promise<NetdiagJob> {
    return this.command('net:kill', { jobId });
  }

  /** This socket's running jobs, plus the API's configured limits. Named to leave BaseService.list alone. */
  listJobs(): Promise<{ jobs: NetdiagJob[], limits: NetdiagLimits }> {
    return this.command('net:list', {});
  }

  onStart(jobId?: string): Observable<NetdiagJob> {
    return this.forJob<NetdiagJob>('net:start', jobId);
  }

  onData(jobId?: string): Observable<NetdiagLine> {
    return this.forJob<NetdiagLine>('net:data', jobId);
  }

  onEnd(jobId?: string): Observable<NetdiagEnd> {
    return this.forJob<NetdiagEnd>('net:end', jobId);
  }

  /**
   * Fires when the socket drops.
   *
   * The API kills a socket's jobs on disconnect, but the resulting `net:end` is
   * emitted to a socket that is already gone, so it never arrives. A caller
   * must treat a disconnect as "every job I started has ended", or a network
   * blip leaves a job running forever in the UI.
   */
  onDisconnect(): Observable<any> {
    return this.socket.fromEvent<any>('disconnect');
  }

  // Events are emitted to the whole socket, not to a subscription, so a second
  // panel's output would otherwise land in this one's console.
  private forJob<T extends { jobId: string }>(event: string, jobId?: string): Observable<T> {
    const stream = this.socket.fromEvent<T>(event);
    return jobId ? stream.pipe(filter(e => e && e.jobId === jobId)) : stream;
  }
}
